
// Security utilities for production

// Sanitize user input to prevent XSS
export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

// Validate email format
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Generate secure random string for tokens
export const generateSecureToken = (length = 32): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const randomArray = new Uint8Array(length);
  crypto.getRandomValues(randomArray);
  
  for (let i = 0; i < length; i++) {
    result += chars[randomArray[i] % chars.length];
  }
  
  return result;
};

// Rate limiting for client-side actions
class RateLimiter {
  private attempts: Map<string, number[]> = new Map();
  
  isAllowed(key: string, maxAttempts = 5, windowMs = 60000): boolean {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Remove old attempts outside the window
    const validAttempts = attempts.filter(time => now - time < windowMs);
    
    if (validAttempts.length >= maxAttempts) {
      return false;
    }
    
    validAttempts.push(now);
    this.attempts.set(key, validAttempts);
    return true;
  }
  
  reset(key: string): void {
    this.attempts.delete(key);
  }
}

export const rateLimiter = new RateLimiter();

// Secure local storage wrapper
export const secureStorage = {
  set: (key: string, value: any): void => {
    try {
      const encrypted = btoa(JSON.stringify(value));
      localStorage.setItem(key, encrypted);
    } catch (error) {
      // Storage failed - handle gracefully
    }
  },
  
  get: (key: string): any => {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;
      return JSON.parse(atob(item));
    } catch (error) {
      return null;
    }
  },
  
  remove: (key: string): void => {
    localStorage.removeItem(key);
  },
  
  clear: (): void => {
    localStorage.clear();
  }
};

// Enhanced HTML sanitization for rich content
export const sanitizeHTML = (input: string): string => {
  if (!input) return '';
  
  // Remove all HTML tags and decode entities for safe display
  const temp = document.createElement('div');
  temp.innerHTML = input;
  return temp.textContent || temp.innerText || '';
};

// Safe HTML rendering for trusted content only
export const renderSafeHTML = (content: string): string => {
  // Only allow basic formatting tags - remove all potentially dangerous elements
  const allowedTags = ['p', 'br', 'strong', 'em', 'u', 'ol', 'ul', 'li'];
  const allowedPattern = new RegExp(`<\\/?(?:${allowedTags.join('|')})\\b[^>]*>`, 'gi');
  
  // First sanitize input, then only allow whitelisted tags
  const sanitized = sanitizeInput(content);
  return sanitized.replace(allowedPattern, (match) => {
    // Only return the match if it's a simple tag without attributes
    return /^<\/?[a-z]+>$/i.test(match) ? match : '';
  });
};

// Enhanced Content Security Policy
const CSP_DIRECTIVES = {
  'default-src': ["'self'"],
  'script-src': ["'self'", "'unsafe-inline'", "'unsafe-eval'", "https://maps.googleapis.com", "https://www.googletagmanager.com"],
  'style-src': ["'self'", "'unsafe-inline'", "https://fonts.googleapis.com", "https://maps.googleapis.com"],
  'img-src': ["'self'", "data:", "https:", "blob:", "https://maps.googleapis.com", "https://maps.gstatic.com"],
  'font-src': ["'self'", "https://fonts.gstatic.com"],
  'connect-src': ["'self'", "https://maps.googleapis.com"],
  'media-src': ["'self'", "https:"],
  'object-src': ["'none'"],
  'child-src': ["'self'"],
  'worker-src': ["'self'", "blob:"],
  'frame-ancestors': ["'none'"],
  'form-action': ["'self'"],
  'base-uri': ["'self'"],
  'upgrade-insecure-requests': []
};

const SECURITY_HEADERS = {
  'Content-Security-Policy': Object.entries(CSP_DIRECTIVES)
    .map(([directive, sources]) => `${directive} ${sources.join(' ')}`)
    .join('; '),
  'X-Frame-Options': 'DENY',
  'X-Content-Type-Options': 'nosniff',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload',
  'Permissions-Policy': 'camera=(), microphone=(), geolocation=(self), payment=(), usb=()',
  'Cross-Origin-Embedder-Policy': 'require-corp',
  'Cross-Origin-Opener-Policy': 'same-origin',
  'Cross-Origin-Resource-Policy': 'same-origin'
};

// CSP Implementation utilities
export const applySecurityHeaders = (response: Response): Response => {
  const headers = new Headers(response.headers);
  
  Object.entries(SECURITY_HEADERS).forEach(([key, value]) => {
    headers.set(key, value);
  });
  
  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers
  });
};

export const createSecureResponse = (body: any, init?: ResponseInit): Response => {
  const response = new Response(body, init);
  return applySecurityHeaders(response);
};

// Security monitoring utilities
export const detectSuspiciousActivity = (userAgent: string, ip: string): boolean => {
  const suspiciousPatterns = [
    /bot|crawler|spider|scraper/i,
    /curl|wget|python|java/i,
    /postman|insomnia/i
  ];
  
  return suspiciousPatterns.some(pattern => pattern.test(userAgent));
};

export const logSecurityEvent = (event: {
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  details: Record<string, any>;
}) => {
  console.warn(`[SECURITY] ${event.severity.toUpperCase()}: ${event.type}`, event.details);
  
  // Store in localStorage for debugging (non-production)
  if (import.meta.env.DEV) {
    const events = JSON.parse(localStorage.getItem('security-events') || '[]');
    events.push({
      ...event,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('security-events', JSON.stringify(events.slice(-100))); // Keep last 100
  }
};
